// Conditional blocks on the check-in questionnaire
// (resources/views/checkin/fields/paragraph.blade.php and checkin/category.blade.php).
//
// Any question can carry a `visible_options` JSON object, which reaches the page as
// data-visible-if (the conditions) and data-visible-else (a selector for a block to show
// instead). A paragraph carries the attributes itself; any other question is wrapped in a
// fieldset by category.blade.php. This module reveals and hides those blocks as the customer answers. The
// data and the attribute names are V1's unchanged, so no question needed editing.
//
// Two deliberate differences from V1's public/assets/js/checkin.js.
//
// It reveals and hides only. V1 also disabled the submit button until the form validated,
// and that WAS the validation - the controller wrote whatever arrived. The server now
// builds its rules from the question set and rejects a bad submission, so there is no
// reason to fight the customer's browser as well.
//
// Blocks render visible and are hidden from here, rather than starting hidden in CSS. V1
// styles .conditional_block as display:none, and on the health declaration the only
// submit button lives inside one - so with JavaScript off that step has no way forward at
// all. Starting visible means the page works without this file, and the server enforces
// the same conditions either way. A medical form should not need JavaScript.
//
// V1's form-wide "nothing moves until every required field is answered" gate is gone with
// it - see evaluate() for why it cannot survive the inversion.
//
// V1 also reads data-required-to-proceed, which it only ever emitted on the Covid step's
// yes/no fields. That step is retired (sort_order 0, and V2 404s it), so nothing emits
// the attribute and nothing here reads it. Re-enabling Covid means restoring both halves.

const FORM_SELECTOR = 'form.questionnaire';
const BLOCK_SELECTOR = '[data-visible-if]';

// The values a field currently holds. A radio group or a checkbox contributes only its
// checked members, so an unanswered group is an empty list and a select on its
// placeholder is too.
function valuesOf(form, name)
{
    return Array.from(form.querySelectorAll('[name="' + name + '"]'))
        .filter(element => (element.type === 'radio' || element.type === 'checkbox')
            ? element.checked
            : element.value !== '')
        .map(element => element.value);
}

function holds(form, name, expected)
{
    return valuesOf(form, name).includes(expected);
}

// Whether a field has been answered at all, as opposed to answered wrongly.
//
// The distinction is the whole point of the three states below, and it depends on the
// control: an unticked checkbox IS an answer ("no, this does not apply to me"), while an
// untouched radio group or an empty select is not an answer yet. A field that is not on
// the page at all counts as answered, since the customer cannot do anything about it.
function isAnswered(form, name)
{
    const elements = Array.from(form.querySelectorAll('[name="' + name + '"]'));

    if (elements.length === 0 || elements.every(element => element.type === 'checkbox'))
    {
        return true;
    }

    return valuesOf(form, name).length > 0;
}

// data-visible-if is a list of single-pair objects, ANDed together:
// [{"1":"no"}, {"2":"no"}]. A name may address a multi-checkbox key ("5[epilepsy]"),
// which is why the field is looked up by its full name rather than a question id.
//
// Three outcomes, not two:
//
//   'met'        - every condition holds.
//   'failed'     - a condition is answered, and answered with something else.
//   'unanswered' - no condition has failed, but at least one has not been answered yet.
//
// 'unanswered' exists because these blocks now render VISIBLE and are hidden from here,
// the reverse of V1 (which hides them in CSS, and so needs no such state). Treating
// not-yet-answered as failure would put the red "please do not attend" panel on screen
// the moment the page loads, before the customer has said anything - and take the health
// declaration's only submit button off it at the same time.
//
// V1 avoided that with a form-wide gate: nothing moved until every required field had a
// value. That gate cannot work here, because "nothing moves" now means "the panel stays
// visible" rather than "the panel stays hidden".
function evaluate(form, conditions)
{
    let unanswered = false;

    for (const pair of Object.values(conditions))
    {
        for (const [name, expected] of Object.entries(pair))
        {
            if (!isAnswered(form, name))
            {
                unanswered = true;
                continue;
            }

            if (!holds(form, name, expected))
            {
                return 'failed';
            }
        }
    }

    return unanswered ? 'unanswered' : 'met';
}

// Uses the hidden attribute rather than a class, so a hidden block is gone for a screen
// reader too. Any submit button inside it is disabled as well: hidden is enough to stop a
// click, but not to stop Enter in a text field reaching the form's first submit button.
// A fieldset is disabled as well, which takes its fields out of the browser's validation
// and out of the submission.
function setHidden(element, hidden)
{
    element.hidden = hidden;

    if (element instanceof HTMLFieldSetElement)
    {
        element.disabled = hidden;
    }

    element.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button =>
    {
        button.disabled = hidden;
    });
}

function refresh(form)
{
    form.querySelectorAll(BLOCK_SELECTOR).forEach(block =>
    {
        let conditions;

        try
        {
            conditions = JSON.parse(block.dataset.visibleIf);
        }
        catch (error)
        {
            // Malformed visible_options. Leave the block as it is rather than guessing:
            // hiding it could take a submit button with it.
            return;
        }

        // Only a determinate failure hides the block and reveals its alternative. While
        // the conditions are merely unanswered the block stays as it rendered, which is
        // visible - so the customer can still reach a submit button that lives inside one.
        const outcome = evaluate(form, conditions);
        const failed = outcome === 'failed';

        // A question waits for its condition to be met: an "if yes, explain" box has no
        // business showing before a yes. A paragraph hides only on a failure, as above.
        setHidden(block, block.dataset.visibleWhen === 'met' ? outcome !== 'met' : failed);

        if (!block.dataset.visibleElse)
        {
            return;
        }

        try
        {
            // The selector comes from the question set, so an admin typo must not take
            // the rest of the form down with it.
            document.querySelectorAll(block.dataset.visibleElse)
                .forEach(target => setHidden(target, !failed));
        }
        catch (error)
        {
            // Not a valid selector; nothing to do.
        }
    });
}

function initForm(form)
{
    // change alone misses a text field until it loses focus, which would leave the
    // customer looking at a page that has not caught up with what they just typed.
    ['change', 'input'].forEach(event => form.addEventListener(event, () => refresh(form)));

    refresh(form);
}

document.addEventListener('DOMContentLoaded', () =>
{
    document.querySelectorAll(FORM_SELECTOR).forEach(initForm);
});
